import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";
import { RolePermission } from "./addRole";

export interface UpdateRolePermissionsPayload {
  role: string;
  permissions: Record<string, boolean>;
}

export interface UpdateRoleHierarchyPayload {
  role: string;
  parentRole: string | null;
}

export interface UpdateRoleResponse {
  success: boolean;
  message: string;
  data: RolePermission;
}

const updateRolePermissions = async (
  payload: UpdateRolePermissionsPayload
): Promise<UpdateRoleResponse> => {
  const { role, permissions } = payload;
  const response = await customAxios.put<UpdateRoleResponse>(
    `/permissions/roles/${role}`,
    { permissions }
  );
  return response.data;
};

const updateRoleHierarchy = async (
  payload: UpdateRoleHierarchyPayload
): Promise<UpdateRoleResponse> => {
  const { role, parentRole } = payload;
  const response = await customAxios.put<UpdateRoleResponse>(
    `/permissions/roles/${role}/hierarchy`,
    { parentRole }
  );
  return response.data;
};

export const useUpdateRolePermissions = () => {
  const queryClient = useQueryClient();

  return useMutation<UpdateRoleResponse, AxiosError, UpdateRolePermissionsPayload>(
    {
      mutationFn: updateRolePermissions,
      onSuccess: async (data) => {
        showNotification({
          title: "Success",
          message: data.message,
          color: "green",
        });
        await queryClient.invalidateQueries(["permissions-roles"]);
      },
      onError: (error) =>
        showNotification({
          title: "Error",
          message:
            (error.response?.data as { message?: string })?.message ??
            "Failed to update role permissions",
          color: "red",
        }),
    }
  );
};

export const useUpdateRoleHierarchy = () => {
  const queryClient = useQueryClient();

  return useMutation<UpdateRoleResponse, AxiosError, UpdateRoleHierarchyPayload>(
    {
      mutationFn: updateRoleHierarchy,
      onSuccess: async (data) => {
        showNotification({
          title: "Success",
          message: data.message,
          color: "green",
        });
        await queryClient.invalidateQueries(["permissions-roles"]);
      },
      onError: (error) =>
        showNotification({
          title: "Error",
          message:
            (error.response?.data as { message?: string })?.message ??
            "Failed to update role hierarchy",
          color: "red",
        }),
    }
  );
};
